import {
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  Add,
  Delete,
  DirectionsCar,
  Info,
  WarningRounded,
} from '@material-ui/icons';
import {
  FormikCheckboxField,
  FormikCurrencyField,
  FormikNumberField,
  FormikTextField,
} from '@superdispatch/forms';
import { formatVehicleType, VEHICLE_TYPES } from '@superdispatch/sdk';
import { ColorDynamic, Inline, useUID } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { useFormikContext } from 'formik';
import { fromPairs, intersectionBy, merge } from 'es-toolkit/compat';
import { Ref, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { castAPIData } from 'shared/api/APISchema';
import { formatPlural } from 'shared/helpers/IntlHelpers';
import { InputTableCell } from 'shared/ui/Table';
import { joinStrings } from 'shared/utils/StringUtils';
import { LoadBulkVinDecodeDrawer } from '../add/LoadBulkVinDecodeDrawer';
import {
  isEmptyLoadVehicleDTO,
  LoadVehicleEditDTO,
  loadVehicleEditSchema,
} from '../data/LoadActionsDTO';
import { LoadVehicleDTO } from '../data/LoadDTO';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import { DuplicatedLoad, normalizeVIN } from '../data/VINsAPI';
import { DuplicatedVINsDialog } from './DuplicatedVINsDialog';
import { FormikVehicleMakeField } from './FormikVehicleMakeField';
import { FormikVehicleModelField } from './FormikVehicleModelField';
import { FormikVINDecoder } from './FormikVINDecoder';
import { LoadVehicleDisabledTooltip } from './LoadVehicleDisabledTooltip';
import styled from 'styled-components';
import { useSubhaulLoad } from '../view/useSubhaulLoad';

const VehicleTypeField = styled(FormikTextField)`
  &[data-has-warning='true'] {
    .MuiInputBase-root {
      outline: 1px solid ${ColorDynamic.Yellow300};
      cursor: default;
    }
    fieldset {
      border-color: ${ColorDynamic.Yellow300};
    }
  }
`;

interface VehicleDeleteDialogProps {
  vehicle?: LoadVehicleDTO;
  onDismiss: () => void;
  onConfirm: () => void;
}

function VehicleDeleteDialog({
  vehicle,
  onDismiss,
  onConfirm,
}: VehicleDeleteDialogProps) {
  const vehicleName = useMemo(
    () =>
      vehicle && joinStrings(' ', vehicle.year, vehicle.make, vehicle.model),
    [vehicle],
  );

  return (
    <Dialog
      maxWidth="xs"
      fullWidth={true}
      onClose={onDismiss}
      open={vehicle != null}
    >
      <DialogTitle>Delete {vehicleName || 'vehicle'}?</DialogTitle>
      <DialogContent>
        All vehicle information including inspection details will be deleted.
      </DialogContent>
      <DialogActions>
        <Button variant="neutral" onClick={onDismiss}>
          Cancel
        </Button>

        <Button type="submit" variant="critical" onClick={onConfirm}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface LoadVehiclesCardProps {
  hasInspections?: boolean;
  nodeRef?: Ref<HTMLElement>;
  isEnabledEditLoad?: boolean;
}

type ModalState =
  | { type: 'bulk_vin_decode'; payload?: never }
  | { type: 'delete_vehicle'; payload: LoadVehicleDTO };

export function LoadVehiclesCard({
  nodeRef,
  hasInspections,
  isEnabledEditLoad = true,
}: LoadVehiclesCardProps) {
  const uid = useUID();
  const vehicleVINFieldRef = useRef<HTMLElement>(null);
  const { isPrimeCarrierSubhaulLoadEditPage } = useSubhaulLoad();
  const { values, setValues } = useFormikContext<{
    vehicles?: LoadVehicleEditDTO[];
  }>();
  const shouldCheckDuplicatedVins = useRef(false);
  const [modalState, setModalState] = useState<ModalState>();
  const [vinTypeWarningsByVin, setVinTypeWarningsByVin] = useState<
    Record<string, true>
  >({});
  const [decodedVinsByVin, setDecodedVinsByVin] = useState<
    Record<string, true>
  >({});
  const [dismissedVinTypeWarningsByVin, setDismissedVinTypeWarningsByVin] =
    useState<Record<string, true>>({});
  const closeModal = () => {
    setModalState(undefined);
  };
  const vehicles = useMemo(() => values.vehicles || [], [values.vehicles]);
  const { duplicateOrders, setDuplicateOrders } = useDuplicatedVins({
    vehicles,
    shouldCheckDuplicatedVins: shouldCheckDuplicatedVins.current,
  });
  const setVehicles = (
    updater: (prev: LoadVehicleEditDTO[]) => LoadVehicleEditDTO[],
  ): void => {
    void setValues((previousValues) => ({
      ...previousValues,
      vehicles: updater(previousValues.vehicles || []),
    }));
  };

  const getVinKey = (vin: string | undefined) => {
    if (!vin) return '';
    return normalizeVIN(vin);
  };

  const clearVinTypeWarning = (vin: string | undefined) => {
    const vinKey = getVinKey(vin);
    if (!vinKey) return;

    setVinTypeWarningsByVin((prev) => {
      if (!prev[vinKey]) return prev;
      const { [vinKey]: _removed, ...next } = prev;
      return next;
    });
  };

  const isWarningVehicleType = (vehicleType: unknown) => {
    if (!vehicleType) return true;
    return String(vehicleType).toLowerCase() === 'other';
  };

  const dismissVinTypeWarning = (vin: string | undefined) => {
    const vinKey = getVinKey(vin);
    if (!vinKey) return;

    setDismissedVinTypeWarningsByVin((prev) => {
      if (prev[vinKey]) return prev;
      return { ...prev, [vinKey]: true };
    });
  };

  const setVinWasDecoded = (vin: string | undefined) => {
    const vinKey = getVinKey(vin);
    if (!vinKey) return;

    setDecodedVinsByVin((prev) => {
      if (prev[vinKey]) return prev;
      return { ...prev, [vinKey]: true };
    });
  };

  const acknowledgeVinTypeWarning = (vin: string | undefined) => {
    clearVinTypeWarning(vin);
    dismissVinTypeWarning(vin);
  };

  const resetDismissedVinTypeWarning = (vin: string | undefined) => {
    const vinKey = getVinKey(vin);
    if (!vinKey) return;

    setDismissedVinTypeWarningsByVin((prev) => {
      if (!prev[vinKey]) return prev;
      const { [vinKey]: _removed, ...next } = prev;
      return next;
    });
  };

  const setVinTypeWarning = (vin: string | undefined) => {
    const vinKey = getVinKey(vin);
    if (!vinKey) return;

    resetDismissedVinTypeWarning(vin);

    setVinTypeWarningsByVin((prev) => {
      if (prev[vinKey]) return prev;
      return { ...prev, [vinKey]: true };
    });
  };

  const shouldFocusFirstVinRef = useRef(false);
  useEffect(() => {
    if (vehicles.length > 1 && shouldFocusFirstVinRef.current) {
      vehicleVINFieldRef.current?.focus();
      shouldFocusFirstVinRef.current = false;
    }
  }, [vehicles.length]);

  return (
    <>
      <DuplicatedVINsDialog
        duplicateVINs={duplicateOrders}
        onClose={() => {
          setDuplicateOrders(undefined);
        }}
      />

      <VehicleDeleteDialog
        onDismiss={closeModal}
        vehicle={
          modalState?.type !== 'delete_vehicle' ? undefined : modalState.payload
        }
        onConfirm={() => {
          closeModal();

          if (modalState?.type === 'delete_vehicle') {
            const { guid } = modalState.payload;

            if (guid) {
              setVehicles((prevVehicles) =>
                prevVehicles.filter((vehicle) => vehicle.guid !== guid),
              );
            }
          }
        }}
      />

      <LoadBulkVinDecodeDrawer
        onClose={closeModal}
        open={modalState?.type === 'bulk_vin_decode'}
        onSubmitSuccess={(decodedVehicles, duplicateLoads) => {
          const duplicatedVins = intersectionBy(
            vehicles,
            decodedVehicles,
            'vin',
          );

          const duplicatedVinsMap: Record<string, DuplicatedLoad[]> = fromPairs(
            duplicatedVins.map((vehicle) => [vehicle.vin, []]),
          );

          setDuplicateOrders(merge(duplicatedVinsMap, duplicateLoads));

          setVehicles((prevVehicles) => {
            const nextVehicles = prevVehicles.filter(
              (vehicle) => !isEmptyLoadVehicleDTO(vehicle),
            );

            nextVehicles.unshift(
              ...decodedVehicles.map((vehicle) =>
                castAPIData(loadVehicleEditSchema, vehicle),
              ),
            );

            return nextVehicles;
          });

          decodedVehicles.forEach((decodedVehicle) => {
            setVinWasDecoded(decodedVehicle?.vin);

            if (isWarningVehicleType(decodedVehicle?.type)) {
              setVinTypeWarning(decodedVehicle?.vin);
              return;
            }

            clearVinTypeWarning(decodedVehicle?.vin);
            resetDismissedVinTypeWarning(decodedVehicle?.vin);
          });

          shouldCheckDuplicatedVins.current = true;
        }}
      />

      <Card ref={nodeRef} aria-label="Vehicles Card">
        <CardContent>
          <Inline verticalAlign="center" space="xsmall" noWrap={true}>
            <DirectionsCar />
            <Typography aria-label="Vehicles Header" variant="h3" id={uid}>
              {vehicles.length === 0
                ? 'No Vehicles'
                : `${vehicles.length} ${formatPlural(vehicles.length, 'Vehicle', 'Vehicles')}`}
            </Typography>

            {!isPrimeCarrierSubhaulLoadEditPage && (
              <>
                <LoadVehicleDisabledTooltip
                  isEnabledEditLoad={isEnabledEditLoad}
                >
                  <Button
                    variant="text"
                    startIcon={<Add />}
                    disabled={!isEnabledEditLoad}
                    onClick={() => {
                      trackLoadsEvent({ name: 'CTMS: Clicked Add Vehicle' });
                      shouldFocusFirstVinRef.current = true;
                      setVehicles((prevVehicles) => [
                        castAPIData(loadVehicleEditSchema, {}),
                        ...prevVehicles,
                      ]);
                    }}
                  >
                    Add
                  </Button>
                </LoadVehicleDisabledTooltip>
                <LoadVehicleDisabledTooltip
                  isEnabledEditLoad={isEnabledEditLoad}
                >
                  <Button
                    variant="text"
                    disabled={!isEnabledEditLoad}
                    onClick={() => {
                      setModalState({ type: 'bulk_vin_decode' });
                    }}
                  >
                    Bulk VIN Decode
                  </Button>
                </LoadVehicleDisabledTooltip>
              </>
            )}
          </Inline>
        </CardContent>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <InputTableCell>VIN</InputTableCell>
                <InputTableCell>Year</InputTableCell>
                <InputTableCell>Make</InputTableCell>
                <InputTableCell>Model</InputTableCell>
                <InputTableCell>Type</InputTableCell>
                <InputTableCell>Color</InputTableCell>
                <InputTableCell>Lot number</InputTableCell>
                <InputTableCell>Price</InputTableCell>
                <InputTableCell width="50px">INOP</InputTableCell>
                <InputTableCell width="90px">
                  <Inline verticalAlign="center" space="xsmall" noWrap={true}>
                    Enclosed
                    <Tooltip
                      placement="top"
                      enterTouchDelay={0}
                      title="The vehicle requires enclosed trailer"
                    >
                      <Info fontSize="small" color="action" />
                    </Tooltip>
                  </Inline>
                </InputTableCell>
                <InputTableCell width="50px" />
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicles.map((vehicle, index) => (
                <TableRow aria-label={`vehicle ${index}`} key={index}>
                  <InputTableCell width="216px">
                    <FormikVINDecoder
                      fullWidth={true}
                      disabled={
                        !isEnabledEditLoad ||
                        !!isPrimeCarrierSubhaulLoadEditPage ||
                        !!vehicle.is_edi
                      }
                      name={`vehicles[${index}].vin`}
                      inputRef={index === 0 ? vehicleVINFieldRef : undefined}
                      inputProps={{
                        'aria-label': `vehicle vin (${index + 1})`,
                        maxLength: 17,
                      }}
                      onDecodeSuccess={(decodedVehicle, duplicatedLoads) => {
                        const vin = vehicles[index]?.vin;
                        setVinWasDecoded(vin);
                        const shouldShowWarning =
                          !decodedVehicle?.type ||
                          String(decodedVehicle?.type).toLowerCase() ===
                            'other';

                        if (shouldShowWarning) {
                          setVinTypeWarning(vin);
                        } else {
                          clearVinTypeWarning(vin);
                          resetDismissedVinTypeWarning(vin);
                        }

                        if (duplicatedLoads.length && decodedVehicle?.vin) {
                          setDuplicateOrders({
                            [decodedVehicle.vin]: duplicatedLoads,
                          });
                        }

                        if (index === 0) {
                          shouldFocusFirstVinRef.current = true;
                        }

                        setVehicles((prevVehicles) => {
                          const nextVehicles = [...prevVehicles];
                          const currentVehicle = nextVehicles[index] ?? {};

                          nextVehicles[index] = castAPIData(
                            loadVehicleEditSchema,
                            {
                              ...currentVehicle,
                              ...decodedVehicle,
                            },
                          );

                          if (index === 0) {
                            nextVehicles.unshift(
                              castAPIData(loadVehicleEditSchema, {}),
                            );
                          }

                          return nextVehicles;
                        });

                        shouldCheckDuplicatedVins.current = true;
                      }}
                    />
                  </InputTableCell>
                  <InputTableCell width="72px">
                    <FormikNumberField
                      fullWidth={true}
                      name={`vehicles[${index}].year`}
                      disabled={
                        !isEnabledEditLoad ||
                        !!isPrimeCarrierSubhaulLoadEditPage ||
                        !!vehicle.is_edi
                      }
                      inputProps={{
                        thousandSeparator: '',
                        'aria-label': `vehicle year (${index + 1})`,
                        maxLength: 4,
                      }}
                    />
                  </InputTableCell>
                  <InputTableCell width="96px">
                    <FormikVehicleMakeField
                      fullWidth={true}
                      disabled={
                        !isEnabledEditLoad ||
                        !!isPrimeCarrierSubhaulLoadEditPage ||
                        !!vehicle.is_edi
                      }
                      name={`vehicles[${index}].make`}
                      inputProps={{
                        'aria-label': `vehicle make (${index + 1})`,
                        maxLength: 200,
                      }}
                    />
                  </InputTableCell>
                  <InputTableCell width="96px">
                    <FormikVehicleModelField
                      fullWidth={true}
                      disabled={
                        !isEnabledEditLoad ||
                        !!isPrimeCarrierSubhaulLoadEditPage ||
                        !!vehicle.is_edi
                      }
                      vehicleMake={vehicles[index]?.make || ''}
                      name={`vehicles[${index}].model`}
                      inputProps={{
                        'aria-label': `vehicle model (${index + 1})`,
                        maxLength: 200,
                      }}
                    />
                  </InputTableCell>
                  <InputTableCell width="128px">
                    {(() => {
                      const vinKey = getVinKey(vehicles[index]?.vin);
                      const isOtherType =
                        String(vehicles[index]?.type).toLowerCase() === 'other';
                      const isEmptyType = !vehicles[index]?.type;
                      const isDismissed =
                        !!dismissedVinTypeWarningsByVin[vinKey];
                      const isDecoded = !!decodedVinsByVin[vinKey];
                      const shouldShowWarning =
                        !isDismissed &&
                        !!vinKey &&
                        (vinTypeWarningsByVin[vinKey] ||
                          (isDecoded && (isOtherType || isEmptyType)));

                      return (
                        <Tooltip
                          arrow={true}
                          placement="top"
                          title={
                            shouldShowWarning
                              ? "Can't identify type. Choose manually"
                              : ''
                          }
                        >
                          <span>
                            <VehicleTypeField
                              select={true}
                              fullWidth={true}
                              disabled={
                                !isEnabledEditLoad ||
                                !!isPrimeCarrierSubhaulLoadEditPage ||
                                !!vehicle.is_edi
                              }
                              name={`vehicles[${index}].type`}
                              SelectProps={{
                                onOpen: () => {
                                  acknowledgeVinTypeWarning(
                                    vehicles[index]?.vin,
                                  );
                                },
                              }}
                              onFocus={(event) => {
                                if (!event.nativeEvent.isTrusted) return;
                                acknowledgeVinTypeWarning(vehicles[index]?.vin);
                              }}
                              inputProps={{
                                'aria-label': `vehicle type (${index + 1})`,
                              }}
                              InputProps={{
                                startAdornment: shouldShowWarning ? (
                                  <InputAdornment position="start">
                                    <WarningRounded
                                      fontSize="small"
                                      htmlColor={ColorDynamic.Yellow300}
                                    />
                                  </InputAdornment>
                                ) : undefined,
                              }}
                              data-has-warning={shouldShowWarning}
                            >
                              {VEHICLE_TYPES.map((key) => (
                                <MenuItem key={key} value={key}>
                                  {formatVehicleType(key)}
                                </MenuItem>
                              ))}
                            </VehicleTypeField>
                          </span>
                        </Tooltip>
                      );
                    })()}
                  </InputTableCell>
                  <InputTableCell width="96px">
                    <FormikTextField
                      disabled={
                        !isEnabledEditLoad ||
                        !!isPrimeCarrierSubhaulLoadEditPage ||
                        !!vehicle.is_edi
                      }
                      name={`vehicles[${index}].color`}
                      inputProps={{
                        'aria-label': `vehicle color (${index + 1})`,
                        maxLength: 200,
                      }}
                    />
                  </InputTableCell>
                  <InputTableCell width="96px">
                    <FormikTextField
                      disabled={
                        !isEnabledEditLoad ||
                        !!isPrimeCarrierSubhaulLoadEditPage
                      }
                      name={`vehicles[${index}].lot_number`}
                      inputProps={{
                        'aria-label': `vehicle lot number (${index + 1})`,
                        maxLength: 200,
                      }}
                    />
                  </InputTableCell>
                  <InputTableCell width="84px">
                    <FormikCurrencyField
                      disabled={
                        !isEnabledEditLoad ||
                        !!isPrimeCarrierSubhaulLoadEditPage
                      }
                      name={`vehicles[${index}].price`}
                      inputProps={{
                        'aria-label': `vehicle price (${index + 1})`,
                      }}
                    />
                  </InputTableCell>
                  <InputTableCell>
                    <FormikCheckboxField
                      disabled={
                        !isEnabledEditLoad ||
                        !!isPrimeCarrierSubhaulLoadEditPage ||
                        !!vehicle.is_edi
                      }
                      label=""
                      name={`vehicles[${index}].is_inoperable`}
                      inputProps={{
                        'aria-label': `vehicle is inoperable (${index + 1})`,
                      }}
                    />
                  </InputTableCell>
                  <InputTableCell>
                    <FormikCheckboxField
                      disabled={
                        !isEnabledEditLoad ||
                        !!isPrimeCarrierSubhaulLoadEditPage ||
                        !!vehicle.is_edi
                      }
                      label=""
                      name={`vehicles[${index}].requires_enclosed_trailer`}
                      inputProps={{
                        'aria-label': `vehicle requires enclosed trailer (${index + 1})`,
                      }}
                    />
                  </InputTableCell>
                  {isEnabledEditLoad &&
                    !isPrimeCarrierSubhaulLoadEditPage &&
                    !vehicle.is_edi && (
                      <InputTableCell padding="none">
                        <IconButton
                          edge="end"
                          aria-label={`remove vehicle (${index + 1})`}
                          onClick={() => {
                            shouldCheckDuplicatedVins.current = false;
                            if (vehicle.guid && hasInspections) {
                              trackLoadsEvent({
                                page: 'edit_load',
                                name: 'CTMS: Clicked Delete Vehicle',
                              });

                              setModalState({
                                payload: vehicle,
                                type: 'delete_vehicle',
                              });
                            } else {
                              setVehicles((prevVehicles) =>
                                prevVehicles.filter(
                                  (_vehicle, idx) => idx !== index,
                                ),
                              );
                            }
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </InputTableCell>
                    )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </>
  );
}

interface DuplicatedVins {
  vehicles: LoadVehicleEditDTO[];
  shouldCheckDuplicatedVins: boolean;
}

function useDuplicatedVins({
  vehicles,
  shouldCheckDuplicatedVins,
}: DuplicatedVins) {
  const [duplicateOrders, setDuplicateOrders] =
    useState<Record<string, DuplicatedLoad[]>>();

  const hasDuplicatedVinLoadForm = useMemo(() => {
    if (shouldCheckDuplicatedVins) {
      return vehicles
        .filter((vehicle) => vehicle.vin)
        .some(
          (vehicle, index, array) =>
            array.findIndex((v) => v.vin === vehicle.vin) !== index,
        );
    }

    return false;
  }, [vehicles, shouldCheckDuplicatedVins]);

  const setDuplicateOrdersCallback = useCallback(() => {
    if (hasDuplicatedVinLoadForm) {
      const duplicatedVinsMap = vehicles
        .filter((vehicle) => vehicle.vin)
        .reduce<Record<string, DuplicatedLoad[]>>((acc, vehicle) => {
          const duplicates = vehicles.filter((v) => v.vin === vehicle.vin);
          if (duplicates.length > 1) acc[vehicle.vin] = [];
          return acc;
        }, {});

      setDuplicateOrders(duplicatedVinsMap);
    }
  }, [hasDuplicatedVinLoadForm, vehicles]);

  useEffect(() => {
    setDuplicateOrdersCallback();
  }, [setDuplicateOrdersCallback]);

  return { duplicateOrders, setDuplicateOrders };
}
